import axios from "axios";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// ✅ GET /:clientId/annual-budgets - Listar presupuestos con filtros
export const getAnnualBudgets = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Agregar filtros opcionales
    if (filters.year) params.append('year', filters.year);
    if (filters.department) params.append('department', filters.department);
    if (filters.category) params.append('category', filters.category);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/annual-budgets${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error getting annual budgets:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuestos",
      status: error.response?.status
    };
  }
};

// ✅ GET /:clientId/annual-budgets/:budgetId - Obtener presupuesto específico
export const getAnnualBudgetById = async (clientId, budgetId) => {
  try {
    const response = await axios.get(`${API_URL}/workflows/p2p/${clientId}/annual-budgets/${budgetId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("Error getting annual budget by ID:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuesto",
      status: error.response?.status
    };
  }
};

// ✅ POST /:clientId/annual-budgets - Crear nuevo presupuesto
export const createAnnualBudget = async (clientId, budgetData) => {
  try {
    const response = await axios.post(`${API_URL}/workflows/p2p/${clientId}/annual-budgets`, budgetData);
    return {
      success: true,
      data: response.data,
      message: "Presupuesto creado exitosamente"
    };
  } catch (error) {
    console.error("Error creating annual budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al crear presupuesto",
      status: error.response?.status,
      details: error.response?.data?.details
    };
  }
};

// ✅ PUT /:clientId/annual-budgets/:budgetId - Actualizar presupuesto existente
export const updateAnnualBudget = async (clientId, budgetId, budgetData) => {
  try {
    const response = await axios.put(`${API_URL}/workflows/p2p/${clientId}/annual-budgets/${budgetId}`, budgetData);
    return {
      success: true,
      data: response.data,
      message: "Presupuesto actualizado exitosamente"
    };
  } catch (error) {
    console.error("Error updating annual budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al actualizar presupuesto",
      status: error.response?.status,
      details: error.response?.data?.details
    };
  }
};

// ✅ DELETE /:clientId/annual-budgets/:budgetId - Eliminar presupuesto
export const deleteAnnualBudget = async (clientId, budgetId) => {
  try {
    const response = await axios.delete(`${API_URL}/workflows/p2p/${clientId}/annual-budgets/${budgetId}`);
    return {
      success: true,
      data: response.data,
      message: "Presupuesto eliminado exitosamente"
    };
  } catch (error) {
    console.error("Error deleting annual budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al eliminar presupuesto",
      status: error.response?.status
    };
  }
};

// ✅ POST /:clientId/annual-budgets/bulk - Crear múltiples presupuestos
export const createBulkAnnualBudgets = async (clientId, budgetsArray) => {
  try {
    const response = await axios.post(`${API_URL}/workflows/p2p/${clientId}/annual-budgets/bulk`, {
      budgets: budgetsArray
    });
    return {
      success: true,
      data: response.data,
      message: `${budgetsArray.length} presupuestos creados exitosamente`
    };
  } catch (error) {
    console.error("Error creating bulk annual budgets:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al crear presupuestos masivamente",
      status: error.response?.status,
      details: error.response?.data?.details,
      failedItems: error.response?.data?.failedItems || []
    };
  }
};

// 📊 Funciones auxiliares para análisis y reportes

// Obtener resumen de presupuestos por año
export const getBudgetSummaryByYear = async (clientId, year) => {
  try {
    const response = await getAnnualBudgets(clientId, { year });
    if (!response.success) return response;

    const budgets = response.data;
    const summary = {
      totalBudgets: budgets.length,
      totalAnnualAmount: 0,
      departmentSummary: {},
      monthlyTotals: {
        january: 0, february: 0, march: 0, april: 0,
        may: 0, june: 0, july: 0, august: 0,
        september: 0, october: 0, november: 0, december: 0
      }
    };

    budgets.forEach(budget => {
      // Total anual
      summary.totalAnnualAmount += budget.total_annual || 0;

      // Resumen por departamento
      if (!summary.departmentSummary[budget.department]) {
        summary.departmentSummary[budget.department] = {
          totalAmount: 0,
          categories: []
        };
      }
      summary.departmentSummary[budget.department].totalAmount += budget.total_annual || 0;
      summary.departmentSummary[budget.department].categories.push(budget.category);

      // Totales mensuales
      Object.keys(summary.monthlyTotals).forEach(month => {
        summary.monthlyTotals[month] += budget[month] || 0;
      });
    });

    return {
      success: true,
      data: summary
    };
  } catch (error) {
    console.error("Error getting budget summary:", error);
    return {
      success: false,
      error: "Error al obtener resumen de presupuestos"
    };
  }
};

// Obtener análisis de variación presupuestal
export const getBudgetVarianceAnalysis = async (clientId, year, comparisonYear = null) => {
  try {
    const currentYearBudgets = await getAnnualBudgets(clientId, { year });
    if (!currentYearBudgets.success) return currentYearBudgets;

    let analysis = {
      year: year,
      totalBudget: 0,
      departmentAnalysis: {},
      monthlyVariance: {}
    };

    // Análisis del año actual
    currentYearBudgets.data.forEach(budget => {
      analysis.totalBudget += budget.total_annual || 0;
      
      if (!analysis.departmentAnalysis[budget.department]) {
        analysis.departmentAnalysis[budget.department] = {
          currentYear: 0,
          previousYear: 0,
          variance: 0,
          variancePercentage: 0
        };
      }
      analysis.departmentAnalysis[budget.department].currentYear += budget.total_annual || 0;
    });

    // Comparación con año anterior si se proporciona
    if (comparisonYear) {
      const previousYearBudgets = await getAnnualBudgets(clientId, { year: comparisonYear });
      if (previousYearBudgets.success) {
        previousYearBudgets.data.forEach(budget => {
          if (analysis.departmentAnalysis[budget.department]) {
            analysis.departmentAnalysis[budget.department].previousYear += budget.total_annual || 0;
          }
        });

        // Calcular variaciones
        Object.keys(analysis.departmentAnalysis).forEach(dept => {
          const current = analysis.departmentAnalysis[dept].currentYear;
          const previous = analysis.departmentAnalysis[dept].previousYear;
          analysis.departmentAnalysis[dept].variance = current - previous;
          analysis.departmentAnalysis[dept].variancePercentage = 
            previous > 0 ? ((current - previous) / previous) * 100 : 0;
        });
      }
    }

    return {
      success: true,
      data: analysis
    };
  } catch (error) {
    console.error("Error getting budget variance analysis:", error);
    return {
      success: false,
      error: "Error al obtener análisis de variación presupuestal"
    };
  }
};

// Validar datos de presupuesto antes de enviar
export const validateBudgetData = (budgetData) => {
  const errors = [];
  
  // Validaciones requeridas
  if (!budgetData.department || budgetData.department.trim() === '') {
    errors.push('El departamento es requerido');
  }
  
  if (!budgetData.category || budgetData.category.trim() === '') {
    errors.push('La categoría es requerida');
  }
  
  if (!budgetData.year || budgetData.year < 2020 || budgetData.year > 2030) {
    errors.push('El año debe estar entre 2020 y 2030');
  }

  // Validar que al menos un mes tenga valor
  const months = ['january', 'february', 'march', 'april', 'may', 'june',
                  'july', 'august', 'september', 'october', 'november', 'december'];
  
  const hasMonthlyData = months.some(month => 
    budgetData[month] && parseFloat(budgetData[month]) > 0
  );
  
  if (!hasMonthlyData) {
    errors.push('Debe asignar presupuesto para al menos un mes');
  }

  // Validar que los valores sean números positivos
  months.forEach(month => {
    if (budgetData[month] && (isNaN(budgetData[month]) || parseFloat(budgetData[month]) < 0)) {
      errors.push(`El valor para ${month} debe ser un número positivo`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

// Formatear datos para carga masiva desde Excel
export const formatBulkBudgetData = (excelData, clientId, year) => {
  try {
    const formattedData = excelData.map((row, index) => {
      // Extraer valores mensuales
      const monthlyValues = {
        january: parseFloat(row.january || row.enero || row.jan || 0),
        february: parseFloat(row.february || row.febrero || row.feb || 0),
        march: parseFloat(row.march || row.marzo || row.mar || 0),
        april: parseFloat(row.april || row.abril || row.apr || 0),
        may: parseFloat(row.may || row.mayo || 0),
        june: parseFloat(row.june || row.junio || row.jun || 0),
        july: parseFloat(row.july || row.julio || row.jul || 0),
        august: parseFloat(row.august || row.agosto || row.aug || 0),
        september: parseFloat(row.september || row.septiembre || row.sep || 0),
        october: parseFloat(row.october || row.octubre || row.oct || 0),
        november: parseFloat(row.november || row.noviembre || row.nov || 0),
        december: parseFloat(row.december || row.diciembre || row.dec || 0)
      };

      // Calcular total anual
      const totalAnual = Object.values(monthlyValues).reduce((sum, value) => sum + value, 0);

      // Mapear al formato de la base de datos
      const budgetItem = {
        department: row.department || row.departamento || '',
        category: row.category || row.categoria || '',
        year: year,
        total: totalAnual,
        monthly: monthlyValues
      };

      // Validar cada item usando la estructura temporal para validación
      const tempValidationItem = {
        department: budgetItem.department,
        category: budgetItem.category,
        year: budgetItem.year,
        ...monthlyValues
      };

      const validation = validateBudgetData(tempValidationItem);
      if (!validation.isValid) {
        throw new Error(`Fila ${index + 1}: ${validation.errors.join(', ')}`);
      }

      return budgetItem;
    });

    return {
      success: true,
      data: formattedData
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al formatear datos de Excel'
    };
  }
};

